import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TableCell from "@mui/material/TableCell";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";

export {
  TableCellSelect as CellSelect,
  TableCellTextField as CellTextField,
} from "common/Components/tableCellForm.styles";

export const StyledPaper = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  maxHeight: "calc(100vh - 11.25rem)",
  minHeight: 360,
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
  border: "none",
  boxShadow: "none",
}));

export const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  flexShrink: 0,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
  gap: theme.spacing(1),
  flexWrap: "wrap",
  justifyContent: "space-between",
  backgroundColor: theme.palette.background.paper,
  zIndex: 2,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const ToolbarActions = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  alignItems: "center",
  marginLeft: "auto",
}));

export const StyledTable = styled(Table)(({ theme }) => ({
  borderCollapse: "collapse",
  backgroundColor: theme.palette.background.paper,
}));

export const StyledTableContainer = styled(TableContainer)(({ theme }) => {
  const headBg = theme.palette.background.paper;

  return {
    flex: 1,
    minHeight: 0,
    maxWidth: "100%",
    overflow: "auto",
    "& table": {
      border: "none !important",
      borderRadius: "0 !important",
    },
    "& thead, & tbody, & tfoot, & tr, & th, & td": {
      border: "none !important",
      borderBottom: "none !important",
    },
    "& .MuiTableHead-root": {
      position: "sticky",
      top: 0,
      zIndex: 5,
      backgroundColor: headBg,
      isolation: "isolate",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: headBg,
        zIndex: 0,
        pointerEvents: "none",
      },
    },
    "& .MuiTableHead-root .MuiTableRow-root": {
      backgroundColor: headBg,
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      position: "relative",
      zIndex: 1,
      backgroundColor: headBg,
      borderBottom: "none",
      overflow: "visible",
    },
    "& .MuiTableHead-root .MuiTableRow-root:first-of-type .MuiTableCell-root": {
      verticalAlign: "middle",
      paddingBottom: theme.spacing(0.5),
      "&::after": {
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: theme.spacing(1.5),
        transform: "translateY(100%)",
        backgroundColor: headBg,
        pointerEvents: "none",
      },
    },
    "& .MuiTableHead-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
      paddingTop: theme.spacing(0.5),
    },
  };
});

export const StyledHeaderCell = styled(TableCell, {
  shouldForwardProp: (p) => p !== "$minWidth",
})(({ theme, $minWidth }) => ({
  minWidth: $minWidth ?? 120,
  fontWeight: 600,
  backgroundColor: theme.palette.background.paper,
  whiteSpace: "normal",
  lineHeight: 1.35,
  verticalAlign: "middle",
  "& .MuiTableSortLabel-root": {
    whiteSpace: "normal",
    lineHeight: 1.35,
    alignItems: "center",
  },
}));

export const StyledFilterCell = styled(TableCell)(({ theme }) => ({
  verticalAlign: "top",
  paddingBottom: theme.spacing(1),
  paddingTop: theme.spacing(0.75),
  backgroundColor: theme.palette.background.paper,
}));

export const EmptyStateText = styled(Typography)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}));

export const EmptyStateLoader = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}));

export const TableFooterBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  flexShrink: 0,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  zIndex: 2,
}));

export const FooterSpacer = styled(Box)({
  flex: 1,
});

export const PaginationWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginLeft: "auto",
});

export const SelectionSummary = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export const FooterSelectionStack = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(0.5),
  minWidth: 0,
  maxWidth: "100%",
  [theme.breakpoints.up("sm")]: {
    maxWidth: 520,
  },
}));

export const StyledTablePagination = styled(TablePagination)(({ theme }) => ({
  border: 0,
  margin: 0,
  padding: 0,
  "& .MuiTablePagination-toolbar": {
    alignItems: "center",
    minHeight: 40,
    paddingLeft: 0,
    paddingRight: 0,
  },
  "& .MuiTablePagination-spacer": {
    display: "none",
  },
  "& .MuiTablePagination-selectLabel": {
    marginBottom: 0,
    marginTop: 0,
    alignSelf: "center",
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.primary,
  },
  "& .MuiTablePagination-displayedRows": {
    marginBottom: 0,
    marginTop: 0,
    alignSelf: "center",
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.primary,
  },
  "& .MuiTablePagination-actions": {
    marginLeft: theme.spacing(1),
    alignSelf: "center",
  },
  "& .MuiTablePagination-input": {
    marginTop: 0,
    marginBottom: 0,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(2),
    alignSelf: "center",
    display: "inline-flex",
    alignItems: "center",
  },
  "& .MuiInputBase-root": {
    marginTop: 0,
    marginBottom: 0,
    alignItems: "center",
  },
  "& .MuiSelect-select": {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: theme.spacing(1.25),
    paddingRight: theme.spacing(3),
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 1.5,
    minHeight: "auto",
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.primary,
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": {
    top: "calc(50% - 0.6em)",
  },
}));

export const VisuallyHidden = styled("span")(visuallyHidden);
